import { CloseOutlined, KeyOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeviceGroupsIcon, DevicesIcon } from 'assets/images/svg';
import { entityType } from 'config/entityType';
import { groupPermissionsBySourceForPerson } from 'library/helpers/accessControl';
import { buildDeviceOptionsWithIntegration, buildGroupOptionsWithType } from 'library/helpers/integrationHelpers';
import { useManagePermittedRoles } from 'library/hooks';
import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectPermissionManageableDevices } from 'reduxStore/devices/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { selectPermissionsByPersonID } from 'reduxStore/permissions/selectors';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { Loader } from 'zComponents/atoms';
import { Button, Col, Divider, Empty, Flex, Form, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const GrantPersonPermissionsModal = ({ visible: visibleProp, setVisible: setVisibleProp, children, personID }) => {
	const {
		token: {
			colorTextLabel,
			fontWeightStrong,
			fontSizeSM,
			paddingXL,
			paddingXS,
			paddingSM,
			paddingLG,
			colorIcon,
			paddingXXS
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const devices = useSelector(selectPermissionManageableDevices);
	const deviceGroups = useSelector(selectAssetGroups);
	const teams = useSelector(selectTeams);

	const allPermissions = useSelector((state) => selectPermissionsByPersonID(state, personID));
	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedDevices, setSelectedDevices] = useState([]);
	const [selectedDeviceGroups, setSelectedDeviceGroups] = useState([]);
	const [removedPermissions, setRemovedPermissions] = useState([]);

	const [hasChanges, setHasChanges] = useState(false);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const canManage = useManagePermittedRoles('aCPermissionManagement');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
		setSelectedDevices([]);
		setSelectedDeviceGroups([]);
		setRemovedPermissions([]);
		setHasChanges(false);
	};

	const handleSubmit = () => {
		// Add new permissions
		if (selectedDevices.length > 0 || selectedDeviceGroups.length > 0) {
			dispatch(
				permissionsActions.grantPermission({
					personIDs: [personID],
					teamID: null, // Explicitly direct access
					deviceIDs: selectedDevices,
					assetGroupIDs: selectedDeviceGroups,
					permissionTypeID: 1
				})
			);
		}

		// Remove permissions
		if (removedPermissions.length > 0) {
			removedPermissions.forEach((perm) => {
				// We use the 'source' to rebuild the exact context for SQL and Firebase
				const isDevice = perm.entityTypeID === entityType.ASSET_DEVICE;
				const isGroupSource = perm.source === 'Group';
				const isTeamSource = perm.source === 'Team';

				dispatch(
					permissionsActions.revokePermission({
						personIDs: [personID],
						permissionTypeID: 1,
						teamID: isTeamSource ? perm.teamID : null,
						assetGroupID: isGroupSource || isTeamSource ? perm.assetGroupID : null,
						deviceIDs: isDevice ? [perm.entityID] : [],
						assetGroupIDs: !isDevice ? [perm.entityID] : []
					})
				);
			});
		}

		handleCancel();
	};

	const displayPermissions = groupPermissionsBySourceForPerson({
		permissions: allPermissions,
		devices,
		deviceGroups,
		teams
	});

	// Filter out permissions that are marked for removal
	const filteredPermissions = displayPermissions.filter(
		(perm) => !removedPermissions.some((removed) => removed.permissionID === perm.permissionID)
	);

	const grantedDeviceIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.ASSET_DEVICE).map((p) => p.entityID)
	);

	const grantedGroupIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.ASSET_GROUPS).map((p) => p.entityID)
	);

	const deviceOptions = buildDeviceOptionsWithIntegration(
		devices.filter((d) => !grantedDeviceIDs.has(d.deviceID) && !selectedDevices.includes(d.deviceID))
	);

	const deviceGroupOptions = buildGroupOptionsWithType(
		deviceGroups.filter((g) => !grantedGroupIDs.has(g.groupID) && !selectedDeviceGroups.includes(g.groupID))
	);

	const handleRemovePermission = (perm) => {
		setRemovedPermissions([...removedPermissions, perm]);
		setHasChanges(true);
	};

	const handleAddDevice = (deviceID) => {
		setSelectedDevices([...selectedDevices, deviceID]);
		setHasChanges(true);
	};

	const handleAddDeviceGroup = (groupID) => {
		setSelectedDeviceGroups([...selectedDeviceGroups, groupID]);
		setHasChanges(true);
	};

	const handleRemoveSelectedDevice = (deviceID) => {
		setSelectedDevices(selectedDevices.filter((id) => id !== deviceID));
		setHasChanges(selectedDevices.length > 1 || selectedDeviceGroups.length > 0 || removedPermissions.length > 0);
	};

	const handleRemoveSelectedDeviceGroup = (groupID) => {
		setSelectedDeviceGroups(selectedDeviceGroups.filter((id) => id !== groupID));
		setHasChanges(selectedDevices.length > 0 || selectedDeviceGroups.length > 1 || removedPermissions.length > 0);
	};

	const renderSelectWithTags = (label, placeholder, options, onSelect) => {
		return (
			<Flex vertical gap={paddingXS}>
				<Text style={{ fontWeight: fontWeightStrong }}>{label}</Text>
				<Select
					style={{ width: '100%', marginBottom: paddingSM }}
					showSearch
					placeholder={placeholder}
					options={options}
					value={null}
					onSelect={onSelect}
					filterOption={(input, option) =>
						(option?.searchLabel || option?.label || '').toString().toLowerCase().includes(input.toLowerCase())
					}
				/>
			</Flex>
		);
	};

	return (
		<>
			<Tooltip title={!canManage ? permissionDeniedMessage : "Manage Person's Permissions"} placement='top'>
				<span onClick={() => canManage && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<KeyOutlined color='currentColor' />}
							disabled={!canManage}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				closable
				onCancel={handleCancel}
				width={763}
				centered
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
				footer={[
					<Fragment key='footer-btns'>
						<Divider />

						<Flex justify='flex-end' gap={12} key='footer'>
							<Button onClick={handleCancel}>Cancel</Button>
							<Button
								type='primary'
								onClick={handleSubmit}
								loading={loadingAddPermission || loadingDeletePermission}
								disabled={!hasChanges}
							>
								Save Changes
							</Button>
						</Flex>
					</Fragment>
				]}
			>
				<Flex vertical style={{ marginBottom: paddingLG }}>
					<Title level={4} style={{ margin: 0, lineHeight: '28px' }}>
						Manage Permissions
					</Title>
					<Text style={{ color: colorTextLabel, lineHeight: '20px', fontSize: fontSizeSM }}>
						Manage this person's permissions by adding or removing devices and device groups from the list.
					</Text>
				</Flex>
				<Form form={form} layout='vertical'>
					<Row>
						<Col span={24}>
							<Flex gap={paddingLG} style={{ marginBottom: paddingLG }} vertical>
								{renderSelectWithTags(
									'Select Device',
									'Enter Device Name(s)',
									deviceOptions,
									handleAddDevice
								)}
								{renderSelectWithTags(
									'Select Device Groups',
									'Enter Device Group Name(s)',
									deviceGroupOptions,
									handleAddDeviceGroup
								)}
							</Flex>
							<Text style={{ fontWeight: fontWeightStrong }}>Current Access Permissions</Text>

							<Flex wrap='wrap' style={{ marginTop: paddingSM }}>
								{loadingDeletePermission ? (
									<Flex justify='center' align='center' style={{ width: '100%' }}>
										<Loader />
									</Flex>
								) : filteredPermissions.length === 0 &&
								  selectedDevices.length === 0 &&
								  selectedDeviceGroups.length === 0 ? (
									<Flex
										align='center'
										justify='center'
										style={{
											width: '100%',
											height: 85
										}}
									>
										<Empty
											image={Empty.PRESENTED_IMAGE_SIMPLE}
											description='No permissions assigned yet. Select devices or device groups above to grant access.'
										/>
									</Flex>
								) : (
									<Flex
										wrap='wrap'
										gap={paddingSM}
										align='flex-start'
										style={{
											height: 85,
											overflowY: 'auto',
											alignContent: 'flex-start',
											alignItems: 'flex-start'
										}}
									>
										{' '}
										{/* Existing permissions (not marked for removal) */}
										{filteredPermissions.map((perm) => (
											<Tooltip key={`existing-${perm.permissionID}`} title={perm.tooltip}>
												<Tag
													closable
													color={perm.badgeColor}
													onClose={() => handleRemovePermission(perm)}
													style={{
														fontSize: fontSizeSM,
														fontWeight: fontWeightStrong,
														display: 'flex',
														alignItems: 'center',
														borderColor: 'currentColor',
														marginInlineEnd: 0
													}}
												>
													{perm.Icon && (
														<perm.Icon color='currentColor' style={{ marginRight: 4 }} />
													)}
													{perm.label}
												</Tag>
											</Tooltip>
										))}
										{/* Newly selected devices */}
										{selectedDevices.map((deviceID) => {
											const device = devices.find((d) => d.deviceID === deviceID);
											return (
												<Tag
													key={`new-device-${deviceID}`}
													color={'gold'}
													style={{
														fontSize: fontSizeSM,
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														opacity: 0.7,
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<DevicesIcon color='currentColor' />
														{device?.name} (New)
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemoveSelectedDevice(deviceID)}
														/>
													</Flex>
												</Tag>
											);
										})}
										{/* Newly selected device groups */}
										{selectedDeviceGroups.map((groupID) => {
											const group = deviceGroups.find((g) => g.groupID === groupID);
											return (
												<Tag
													key={`new-group-${groupID}`}
													color={'blue'}
													style={{
														fontSize: fontSizeSM,
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														opacity: 0.7,
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<DeviceGroupsIcon color='currentColor' />
														{group?.name} (New)
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemoveSelectedDeviceGroup(groupID)}
														/>
													</Flex>
												</Tag>
											);
										})}
									</Flex>
								)}
							</Flex>
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	);
};

export default GrantPersonPermissionsModal;
